/**
 * @file pwa/offline-api-cache.ts
 * @description API JSON 的 online-first IndexedDB cache。
 *
 * 用途：
 * - 在線時請求 API 並把 data 寫入 cache_api。
 * - 離線或網路/5xx 失敗時讀 cache_api。
 * - 4xx 業務錯誤預設不吞掉，避免用過期快取掩蓋權限/校驗問題。
 */
import { ApiError, apiClient } from '@/lib/api-client';
import { getOfflineDb } from '@/pwa/db';

export class OfflineCacheMissError extends Error {
  constructor(public url: string) {
    super(`Offline cache miss: ${url}`);
    this.name = 'OfflineCacheMissError';
  }
}

export interface CacheOptions {
  taskId?: number;
  scope?: string;
}

export interface FetchWithOfflineCacheOptions extends CacheOptions {
  /** 強制只讀 IndexedDB cache，不打網路。 */
  forceCache?: boolean;
  /** 4xx 時也允許 fallback cache；預設 false。 */
  fallbackOnClientError?: boolean;
}

export async function putCachedApi(
  url: string,
  data: unknown,
  opts: CacheOptions = {},
): Promise<void> {
  const db = await getOfflineDb();
  await db.put('cache_api', {
    url,
    data,
    taskId: opts.taskId,
    scope: opts.scope ?? 'api',
    cachedAt: Date.now(),
  });
}

export async function getCachedApi<T>(url: string): Promise<T | null> {
  const db = await getOfflineDb();
  const row = await db.get('cache_api', url);
  return (row?.data as T | undefined) ?? null;
}

export async function deleteCachedApi(url: string): Promise<void> {
  const db = await getOfflineDb();
  await db.delete('cache_api', url);
}

export async function fetchWithOfflineCache<T>(
  url: string,
  opts: FetchWithOfflineCacheOptions = {},
): Promise<{ data: T; source: 'network' | 'cache'; cachedAt?: number }> {
  if (opts.forceCache || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    return readCachedOrThrow<T>(url);
  }

  try {
    const data = await apiClient.get<T>(url);
    await putCachedApi(url, data, opts);
    return { data, source: 'network' };
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status >= 400 &&
      err.status < 500 &&
      !opts.fallbackOnClientError
    ) {
      throw err;
    }
    return readCachedOrThrow<T>(url);
  }
}

async function readCachedOrThrow<T>(
  url: string,
): Promise<{ data: T; source: 'cache'; cachedAt?: number }> {
  const db = await getOfflineDb();
  const row = await db.get('cache_api', url);
  if (!row) throw new OfflineCacheMissError(url);
  return { data: row.data as T, source: 'cache', cachedAt: row.cachedAt };
}
